/**
 * 应用状态管理Hook
 */
import { useState, useCallback } from 'react';
import { AppState, ConfigData, OutlineData, ProjectConfig } from '../types';
import { draftStorage } from '../utils/draftStorage';

const initialState: AppState = {
  projectId: undefined,
  currentStep: 0,
  maxReachedStep: 0,
  config: {
    api_key: '',
    base_url: '',
    model_name: 'gpt-3.5-turbo',
  },
  projectConfig: {
    projectName: '',
    alias: '',
    company: null,
    projectType: '通用类',
    wordCount: 50000,
    aiModelName: '千问',
    selectedModel: '',
    projectPrice: 0,
  },
  fileContent: '',
  projectOverview: '',
  techRequirements: '',
  outlineRequirements: '',
  disqualification: '',
  quotation: '', // 新增：招标解析中的分项报价内容
  templates: '', // 新增：招标解析中的招标模版内容
  quotationData: null, // 新增：分项报价步骤的数据
  outlineData: null,
  selectedChapter: '',
  stepsCompleted: {
    analysis: false,
    quotation: false, // 新增：分项报价是否完成
    outline: false,
  },
};

export const useAppState = () => {
  const [state, setState] = useState<AppState>(() => {
    const draft = draftStorage.loadDraft();
    return {
      ...initialState,
      ...(draft || {}),
    };
  });

  const updateConfig = useCallback((config: ConfigData) => {
    setState(prev => ({ ...prev, config }));
  }, []);

  const updateProjectConfig = useCallback((projectConfig: ProjectConfig) => {
    setState(prev => {
      const next = { ...prev, projectConfig };
      draftStorage.saveDraft({ projectConfig });
      return next;
    });
  }, []);

  const updateStep = useCallback((step: number) => {
    setState(prev => {
      const newMaxStep = Math.max(prev.maxReachedStep, step);
      const next = { 
        ...prev, 
        currentStep: step,
        maxReachedStep: newMaxStep,
      };
      draftStorage.saveDraft({ 
        currentStep: step,
        maxReachedStep: newMaxStep,
      });
      return next;
    });
  }, []);

  const updateFileContent = useCallback((fileContent: string) => {
    setState(prev => {
      const next = { ...prev, fileContent };
      draftStorage.saveDraft({ fileContent });
      return next;
    });
  }, []);

  const updateAnalysisResults = useCallback((overview: string, requirements: string, outlineReqs: string, disqualification: string) => {
    setState(prev => {
      const next = {
        ...prev,
        projectOverview: overview,
        techRequirements: requirements,
        outlineRequirements: outlineReqs,
        disqualification: disqualification,
      };
      draftStorage.saveDraft({
        projectOverview: overview,
        techRequirements: requirements,
        outlineRequirements: outlineReqs,
        disqualification: disqualification,
      });
      return next;
    });
  }, []);

  const updateOutline = useCallback((outlineData: OutlineData) => {
    setState(prev => {
      const next = { ...prev, outlineData };
      draftStorage.saveDraft({ outlineData });
      return next;
    });
  }, []);

  // 更新招标解析中的分项报价内容
  const updateQuotation = useCallback((quotation: string) => {
    setState(prev => {
      const next = { ...prev, quotation };
      draftStorage.saveDraft({ quotation });
      return next;
    });
  }, []);

  const updateTemplates = useCallback((templates: string) => {
    setState(prev => {
      const next = { ...prev, templates };
      draftStorage.saveDraft({ templates });
      return next;
    });
  }, []);

  // 新增：更新分项报价数据
  const updateQuotationData = useCallback((quotationData: any) => {
    setState(prev => {
      const next = { ...prev, quotationData };
      draftStorage.saveDraft({ quotationData });
      return next;
    });
  }, []);

  const updateSelectedChapter = useCallback((chapterId: string) => {
    setState(prev => {
      const next = { ...prev, selectedChapter: chapterId };
      draftStorage.saveDraft({ selectedChapter: chapterId });
      return next;
    });
  }, []);

  // 标记招标解析完成
  const markAnalysisCompleted = useCallback(() => {
    setState(prev => ({
      ...prev,
      stepsCompleted: { ...prev.stepsCompleted, analysis: true },
    }));
  }, []);

  // 标记分项报价完成
  const markQuotationCompleted = useCallback(() => {
    setState(prev => ({
      ...prev,
      stepsCompleted: { ...prev.stepsCompleted, quotation: true },
    }));
  }, []);

  // 标记目录生成完成
  const markOutlineCompleted = useCallback(() => {
    setState(prev => {
      const next = {
        ...prev,
        stepsCompleted: { ...prev.stepsCompleted, outline: true },
      };
      draftStorage.saveDraft({ stepsCompleted: next.stepsCompleted });
      return next;
    });
  }, []);

  const nextStep = useCallback(() => {
    setState(prev => {
      const nextStepValue = Math.min(prev.currentStep + 1, 4); // 改为4步
      const next = { ...prev, currentStep: nextStepValue };
      draftStorage.saveDraft({ currentStep: nextStepValue });
      return next;
    });
  }, []);

  const prevStep = useCallback(() => {
    setState(prev => {
      const prevStepValue = Math.max(prev.currentStep - 1, 0);
      const next = { ...prev, currentStep: prevStepValue };
      draftStorage.saveDraft({ currentStep: prevStepValue });
      return next;
    });
  }, []);
  
  // 重置后续步骤的数据（用于点击 NEXT 时）
  const resetSubsequentSteps = useCallback((fromStep: number) => {
    console.log('=== resetSubsequentSteps 被调用 ===', { fromStep });
    setState(prev => {
      const updates: any = {};
      
      // 根据fromStep重置后续步骤
      if (fromStep <= 1) {
        // 重置招标解析步骤
        updates.projectOverview = '';
        updates.techRequirements = '';
        updates.outlineRequirements = '';
        updates.disqualification = '';
        updates.stepsCompleted = { ...prev.stepsCompleted, analysis: false };
      }
      if (fromStep <= 2) {
        // 重置分项报价完成状态，但不清空数据（数据保存在后端）
        updates.stepsCompleted = { ...prev.stepsCompleted, quotation: false };
      }
      if (fromStep <= 3) {
        // 重置大纲编辑步骤
        updates.outlineData = null;
        updates.stepsCompleted = { ...prev.stepsCompleted, outline: false };
      }
      if (fromStep <= 4) {
        console.log('清空步骤5的章节内容', { hasOutlineData: !!prev.outlineData });
        // 重置内容编辑步骤：清空所有章节内容，保留大纲结构
        if (prev.outlineData) {
          const clearContent = (item: any): any => {
            const cleared = { ...item, content: '' };
            if (item.children && item.children.length > 0) {
              cleared.children = item.children.map(clearContent);
            }
            return cleared;
          };
          
          updates.outlineData = {
            ...prev.outlineData,
            outline: prev.outlineData.outline.map(clearContent),
          };
          console.log('已清空 outlineData 的内容', updates.outlineData);
          
          // 同时清空 localStorage 中的章节缓存
          draftStorage.clearAllContent();
          console.log('已清空 localStorage 中的章节缓存');
        }
        updates.selectedChapter = '';
      }
      
      const next = { ...prev, ...updates };
      draftStorage.saveDraft(updates);
      console.log('=== resetSubsequentSteps 完成 ===', { updates });
      return next;
    });
  }, []);
  
  // 同时更新 currentStep 和 maxReachedStep（用于点击 NEXT 重置后续步骤时）
  const updateStepAndResetMax = useCallback((step: number) => {
    setState(prev => {
      const next = {
        ...prev,
        currentStep: step,
        maxReachedStep: step,
      };
      draftStorage.saveDraft({
        currentStep: step,
        maxReachedStep: step,
      });
      return next;
    });
  }, []);
  
  // 设置项目 ID
  const setProjectId = useCallback((projectId: string) => {
    setState(prev => {
      const next = { ...prev, projectId };
      draftStorage.saveDraft({ projectId });
      return next;
    });
  }, []);
  
  // 加载项目数据（从数据库恢复项目状态）
  const loadProject = useCallback((projectData: any) => {
    const newState: Partial<AppState> = {
      projectId: projectData.id,
      projectConfig: {
        projectName: projectData.name,
        alias: projectData.alias || '',
        company: projectData.company_credit_code ? {
          id: projectData.company_credit_code,  // 使用统一社会信用代码
          name: projectData.company?.name || '',
          fullName: projectData.company?.full_name || ''
        } : null,
        projectType: projectData.project_type || '通用类',
        wordCount: projectData.word_count || 50000,
        aiModelName: projectData.config?.ai_model_name || '千问',
        selectedModel: projectData.config?.selected_model || '',
        projectPrice: projectData.project_price || 0,
        groupId: projectData.group_id || undefined,
      },
      currentStep: projectData.progress?.current_step || 0,
      maxReachedStep: projectData.progress?.max_reached_step || 0,
      stepsCompleted: {
        analysis: projectData.progress?.analysis_completed || false,
        quotation: false, // 分项报价默认未完成
        outline: projectData.progress?.outline_completed || false,
      },
    };
    
    // 加载招标文件
    if (projectData.files && projectData.files.length > 0) {
      newState.fileContent = projectData.files[0].file_content || '';
    }
    
    // 加载解析结果
    const analysisMap: any = {};  // 将analysisMap提升到外层作用域
    if (projectData.analyses) {
      projectData.analyses.forEach((analysis: any) => {
        analysisMap[analysis.analysis_type] = analysis.content;
      });
      newState.projectOverview = analysisMap.overview || '';
      newState.techRequirements = analysisMap.requirements || '';
      newState.outlineRequirements = analysisMap.outline_requirements || '';
      newState.disqualification = analysisMap.disqualification || '';
      newState.quotation = analysisMap.quotation || ''; // 加载招标解析中的分项报价
      newState.templates = analysisMap.templates || ''; // 加载招标解析中的招标模版
      
      // 加载分项报价数据
      if (analysisMap.quotation_filled) {
        try {
          newState.quotationData = JSON.parse(analysisMap.quotation_filled);
        } catch (e) {
          console.error('解析报价数据失败:', e);
        }
      }
    }
    
    // 加载目录
    if (projectData.outline_items && projectData.outline_items.length > 0) {
      // 后端已返回嵌套树形结构（children 已填充），递归转换字段名
      const convertOutlineItems = (items: any[]): any[] => {
        return items.map((item: any) => ({
          id: item.item_id,
          title: item.title,
          description: item.description || '',
          expected_word_count: item.expected_word_count || 0,
          actual_word_count: item.actual_word_count || 0,
          is_locked: item.is_locked || false,
          children: item.children && item.children.length > 0
            ? convertOutlineItems(item.children)
            : [],
        }));
      };

      // 只处理根节点（parent_id 为 null），子节点通过 children 递归处理
      const rootItems = projectData.outline_items.filter((item: any) => !item.parent_id);
      newState.outlineData = {
        outline: convertOutlineItems(rootItems),
        project_name: projectData.name,
        project_overview: analysisMap.overview || newState.projectOverview || '',
      };
    }
    
    // 加载章节内容（恢复到 localStorage）
    // 注意：先清空 localStorage，然后加载新项目的章节内容
    if (projectData.chapters && projectData.chapters.length > 0) {
      const chapterContentMap: Record<string, string> = {};
      
      // 首先构建 outline_item_id 到 item_id 的映射
      const outlineItemIdMap: Record<number, string> = {};
      const buildIdMap = (items: any[]) => {
        items.forEach((item: any) => {
          outlineItemIdMap[item.id] = item.item_id;  // 数据库 id -> 前端 item_id
          if (item.children) {
            buildIdMap(item.children);
          }
        });
      };
      
      if (projectData.outline_items) {
        buildIdMap(projectData.outline_items);
      }
      
      // 将章节内容映射到前端 item_id
      projectData.chapters.forEach((chapter: any) => {
        const itemId = outlineItemIdMap[chapter.outline_item_id];
        if (itemId && chapter.content) {
          chapterContentMap[itemId] = chapter.content;
        }
      });
      
      // 先清空，再保存新项目的章节内容
      draftStorage.clearAllContent();
      draftStorage.saveContentById(chapterContentMap);
      console.log('📝 加载章节内容:', Object.keys(chapterContentMap).length, '个章节');
    } else {
      // 如果没有章节数据，也要清空 localStorage
      draftStorage.clearAllContent();
      console.log('📝 新项目没有章节内容，清空 localStorage');
    }
    
    setState(prev => {
      const next = { ...prev, ...newState };
      draftStorage.saveDraft(newState);
      return next;
    });
  }, []);
  
  // 重置项目（新建标书）
  const resetProject = useCallback(() => {
    setState(initialState);
    draftStorage.clearAll();
  }, []);

  return {
    state,
    updateConfig,
    updateProjectConfig,
    updateStep,
    updateFileContent,
    updateAnalysisResults,
    updateOutline,
    updateQuotation, // 新增
    updateTemplates, // 新增：更新招标模版
    updateQuotationData, // 新增：更新分项报价数据
    updateSelectedChapter,
    markAnalysisCompleted,
    markQuotationCompleted,
    markOutlineCompleted,
    nextStep,
    prevStep,
    resetSubsequentSteps,
    updateStepAndResetMax,
    setProjectId,
    loadProject,
    resetProject,
  };
};